import React, { useMemo, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Avatar,
  IconButton,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Box,
  Skeleton
} from '@mui/material';
import { FiMenu } from 'react-icons/fi';
import { useLocation } from 'react-router-dom';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useGetCurrentUser } from 'apis/user/hook';
import * as urls from 'routes/config';
import { useLogout } from 'apis/auth/hook';
import BusinessDropdown from './BusinessDropdown';
import { useGetBusinessProfile } from 'apis/business/hook';
import { useBusiness } from 'providers/context/BusinessProvider';
import SettingIcon from 'assets/icons/SettingIcon';
import { useGetOrganizations } from 'apis/agency/organizations/hook';
import LocationDropdown from './LocationDropdown';
import BusinessSettingIcon from '@/assets/icons/BusinessSettingIcon';
import BusinessDropdownIcon from '@/assets/icons/BusinessDropdownIcon';
import BusinessNotificationBell from '@/components/Notifications/BusinessNotificationBell';
import ReferralGiftIcon from '@/assets/icons/ReferralGiftIcon';
import ReferralModal from '@/components/Common/ReferralModal';
import TimezoneIndicator from './TimezoneIndicator';
import { useGetLocations } from '@/apis/business/setting/location/hook';

interface HeaderProps {
  onMenuClick?: () => void;
}

export default function Header({ onMenuClick }: HeaderProps) {
  const navigate = useNavigate();
  const { locationId } = useParams();
  const theme = useTheme();
  const { selectedBusiness } = useBusiness();
  const { mutate: doLogout } = useLogout();
  const { data: organizations, isLoading: loadingOrganization } = useGetOrganizations();
  const location = useLocation();

  const isSettingPage = location.pathname.includes('/setting');

  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { data: currentUser, isLoading: isCurrentUserLoading } = useGetCurrentUser();

  const isAgencyUser = ['agency_admin', 'agency_admin_readonly', 'agent_builder'].includes(currentUser?.role ?? '');
  const hideNotificationBell = isAgencyUser;

  const { data: currentBusiness, isLoading: isCurrentBusinessLoading } = useGetBusinessProfile(undefined, {
    queryKey: ['businessProfile', locationId],
    enabled: Boolean(!isAgencyUser && !isCurrentUserLoading)
  });

  const { data: locations } = useGetLocations({ include: 'address' }, {
    enabled: Boolean(selectedBusiness && locationId)
  } as any);

  const profilePageRoute = isAgencyUser ? '/agency/profile' : `${urls.base(locationId)}/${urls.home.profile}`;

  const renderBusinessTitle = (() => {
    if (isAgencyUser) return <BusinessDropdown />;
    if (isCurrentBusinessLoading || isCurrentUserLoading) return <BusinessDropdown isLoading={true} />;
    return (
      <Box border='1px solid #ECECEC' px={1} py={0.5} borderRadius='8px'>
        <Typography fontSize={13} color='secondary'>
          Current Business
        </Typography>

        <Box display='flex' flexDirection='row' alignItems='center' gap={0.5} height={15} pb={0.5} width={200}>
          <Box color={theme.palette.primary.main}>
            <BusinessDropdownIcon />
          </Box>

          <Typography noWrap fontWeight={600} sx={{ fontSize: { xs: '12px', lg: 14 } }}>
            {currentBusiness?.name}
          </Typography>
        </Box>
      </Box>
    );
  })();

  const timezone = useMemo(() => {
    const locationList = locations?.data ?? [];

    const activeLocation = locationList.find((loc) => loc.id === Number(locationId));

    return activeLocation?.address?.time_zone ?? currentBusiness?.address?.time_zone ?? null;
  }, [locations?.data, locationId, currentBusiness?.address?.time_zone]);

  const renderLocation = (() => {
    if (!locationId) return null;
    return <LocationDropdown />;
  })();

  return (
    <AppBar
      position='fixed'
      sx={{
        backgroundColor: theme.palette.background.paper,
        boxShadow: 'none',
        borderBottom: '1px solid #E0E0E0',
        top: 0,
        maxHeight: { xs: 56, lg: 64 },
        zIndex: 1200
      }}
    >
      <Toolbar
        sx={{
          justifyContent: isMediumScreen ? 'space-between' : 'space-between',
          minHeight: { xs: 56, lg: 64 },
          px: { xs: 1.5, lg: 3 }
        }}
      >
        {/* MOBILE VIEW HEADER */}
        {isMediumScreen ? (
          <>
            <Box display='flex' alignItems='center' gap={1}>
              <IconButton onClick={onMenuClick} sx={{ p: 0.75 }}>
                <FiMenu size={22} />
              </IconButton>
            </Box>

            {/* Center Title */}
            <Box
              component={Link}
              to='/'
              sx={{
                display: 'flex',
                alignItems: 'center',
                '& img': {
                  height: { xs: 32, lg: 40 },
                  paddingRight: { xs: 1, lg: 1.25 },
                  borderRight: isMediumScreen ? 'none' : '1px solid #cecece'
                }
              }}
            >
              <img src={organizations?.logo_url} alt={organizations?.name} />
            </Box>

            <Box display='flex' alignItems='center' gap={0.5}>
              {currentUser?.role !== 'agency_admin' && currentBusiness?.tolt_enabled && (
                <Box
                  onClick={() => setIsReferralModalOpen(true)}
                  alignItems='center'
                  borderRadius='6px'
                  gap={0.5}
                  sx={{
                    display: 'flex',
                    border: `1px solid ${theme.palette.primary.main}`,
                    backgroundColor: theme.palette.primary.light,
                    padding: '4px 8px',
                    cursor: 'pointer'
                  }}
                >
                  <ReferralGiftIcon />
                  <Typography
                    fontWeight={500}
                    sx={{ color: theme.palette.primary.main, fontSize: { xs: '0.75rem', lg: '0.875rem' } }}
                  >
                    Refer & Earn $50{' '}
                    <Typography component='span' fontSize='10px'>
                      Per Referral
                    </Typography>
                  </Typography>
                </Box>
              )}

              <Box display='flex' alignItems='center' gap={0.5}>
                {!isCurrentUserLoading && !hideNotificationBell && <BusinessNotificationBell />}
                {isCurrentUserLoading ? (
                  <Skeleton variant='circular' width={32} height={32} />
                ) : (
                  <IconButton onClick={handleClick} sx={{ p: 0 }}>
                    <Avatar
                      src={currentUser?.avatar_url}
                      alt={currentUser?.name || 'User'}
                      sx={{ width: 32, height: 32 }}
                    />
                  </IconButton>
                )}
              </Box>

              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { width: 205 } } }}
              >
                <MenuItem onClick={() => navigate(profilePageRoute)}>Profile</MenuItem>
                <MenuItem onClick={() => doLogout()}>Logout</MenuItem>
              </Menu>
            </Box>
          </>
        ) : (
          /* DESKTOP VIEW - UNCHANGED */
          <>
            <Box display='flex' alignItems='center' color='#09090A' gap={{ xs: 0.5, lg: 2 }} flex={1} minWidth={0}>
              <Box
                component={Link}
                to='/'
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  '& img': {
                    height: { xs: 32, lg: 40 },
                    paddingRight: { xs: 1, lg: 1.25 },
                    borderRight: isMediumScreen ? 'none' : '1px solid #cecece'
                  }
                }}
              >
                {loadingOrganization ? (
                  <Skeleton
                    variant='rectangular'
                    animation='wave'
                    sx={{
                      height: { xs: 32, lg: 32 },
                      width: { xs: 100, lg: 100 },
                      borderRadius: '6px'
                    }}
                  />
                ) : (
                  <img src={organizations?.logo_url} alt={organizations?.name} />
                )}
              </Box>
              {selectedBusiness && (
                <Link to={urls.business.base}>
                  <IconButton
                    color='primary'
                    sx={{
                      color: theme.palette.primary.main,
                      display: isMediumScreen ? 'none' : 'inherit',
                      padding: { xs: '4px', lg: '8px' },
                      '&:hover': {
                        backgroundColor: 'transparent'
                      }
                    }}
                  >
                    <Box display='flex'>{isSettingPage ? <SettingIcon /> : <BusinessSettingIcon />}</Box>
                  </IconButton>
                </Link>
              )}
              {renderBusinessTitle}
              {renderLocation}
              <TimezoneIndicator timezone={timezone} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, lg: 1 }, flexShrink: 0 }}>
              {isCurrentUserLoading ? (
                <>
                  <Skeleton
                    variant='text'
                    width={80}
                    height={20}
                    sx={{
                      marginRight: 1,
                      display: isMediumScreen ? 'none' : 'block'
                    }}
                  />
                  <Skeleton
                    variant='circular'
                    width={34}
                    height={34}
                    sx={{
                      display: isMediumScreen ? 'none' : 'block'
                    }}
                  />
                </>
              ) : (
                <>
                  {currentUser?.role !== 'agency_admin' && currentBusiness?.tolt_enabled && (
                    <Box
                      onClick={() => setIsReferralModalOpen(true)}
                      alignItems='center'
                      borderRadius='6px'
                      gap={0.5}
                      sx={{
                        display: 'flex',
                        border: `1px solid ${theme.palette.primary.main}`,
                        backgroundColor: theme.palette.primary.light,
                        padding: '6px 16px',
                        cursor: 'pointer'
                      }}
                    >
                      <ReferralGiftIcon />
                      <Typography fontWeight={500} sx={{ color: theme.palette.primary.main }} fontSize='14px'>
                        Refer & Earn $50{' '}
                        <Typography component='span' fontSize='10px'>
                          Per Referral
                        </Typography>
                      </Typography>
                    </Box>
                  )}
                  {!hideNotificationBell && (
                    <Box sx={{ display: isMediumScreen ? 'none' : 'flex', alignItems: 'center' }}>
                      <BusinessNotificationBell />
                    </Box>
                  )}
                  <Typography
                    variant='body1'
                    sx={{
                      color: '#09090A',
                      marginRight: { xs: 0.5, lg: 1 },
                      display: isMediumScreen ? 'none' : 'inherit',
                      fontSize: { xs: '0.875rem', lg: '1rem' }
                    }}
                  >
                    {currentUser?.name}
                  </Typography>
                  <IconButton
                    onClick={handleClick}
                    sx={{
                      display: isMediumScreen ? 'none' : 'inherit',
                      padding: { xs: '4px', lg: '8px' }
                    }}
                  >
                    <Avatar
                      src={currentUser?.avatar_url}
                      alt={currentUser?.name || 'Dummy'}
                      sx={{
                        width: { xs: 28, lg: 34 },
                        height: { xs: 28, lg: 34 }
                      }}
                    />
                  </IconButton>
                </>
              )}
              {!isMediumScreen && !isCurrentUserLoading && (
                <Menu
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                  }}
                  slotProps={{ paper: { sx: { width: 205 } } }}
                >
                  <MenuItem onClick={() => navigate(profilePageRoute)}>Profile</MenuItem>
                  <MenuItem onClick={() => doLogout()}>Logout</MenuItem>
                </Menu>
              )}
            </Box>
          </>
        )}
      </Toolbar>
      <ReferralModal open={isReferralModalOpen} onClose={() => setIsReferralModalOpen(false)} />
    </AppBar>
  );
}
